/**
 * OWLY CRM — Lead Field Choices (Single Source of Truth)
 *
 * ALL lead option arrays and label maps live here.
 * Values mirror the Django model Lead.*_CHOICES exactly.
 *
 * Usage:
 *   import { GENDER_OPTIONS, GENDER_LABELS } from '@/shared/constants/leadFieldChoices';
 *
 *   // For <select> components:
 *   <select>{GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
 *
 *   // For display/lookup:
 *   const text = GENDER_LABELS[lead.gender] || lead.gender;
 */

// ── Shared option type ──
export interface FieldOption {
  value: string;
  label: string;
}

// Helper to derive label maps from option arrays
function toLabelMap(options: readonly FieldOption[]): Record<string, string> {
  return Object.fromEntries(options.map(o => [o.value, o.label]));
}

// ══════════════════════════════════════════════
// OPTIONS ARRAYS (for <select> / dropdown)
// ══════════════════════════════════════════════

export const GENDER_OPTIONS: FieldOption[] = [
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Femenino' },
  { value: 'other', label: 'Otro' },
];

export const MARITAL_STATUS_OPTIONS: FieldOption[] = [
  { value: 'soltero', label: 'Soltero(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'union_libre', label: 'Unión Libre' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viudo', label: 'Viudo(a)' },
];

export const EDUCATION_LEVEL_OPTIONS: FieldOption[] = [
  { value: 'primaria', label: 'Primaria' },
  { value: 'secundaria', label: 'Secundaria' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'tecnologo', label: 'Tecnólogo' },
  { value: 'profesional', label: 'Profesional' },
  { value: 'posgrado', label: 'Posgrado' },
];

export const HOUSING_STATUS_OPTIONS: FieldOption[] = [
  { value: 'arriendo', label: 'Arriendo' },
  { value: 'propia', label: 'Vivienda Propia' },
  { value: 'familiar', label: 'Vivienda Familiar' },
];

export const PET_TYPE_OPTIONS: FieldOption[] = [
  { value: 'dog', label: 'Perro' },
  { value: 'cat', label: 'Gato' },
  { value: 'both', label: 'Perro y Gato' },
  { value: 'other', label: 'Otro' },
];

export const HAS_PETS_OPTIONS: FieldOption[] = [
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' },
];

export const DOCUMENT_TYPE_OPTIONS: FieldOption[] = [
  { value: 'CC', label: 'Cédula de Ciudadanía' },
  { value: 'CE', label: 'Cédula de Extranjería' },
  { value: 'TI', label: 'Tarjeta de Identidad' },
  { value: 'PA', label: 'Pasaporte' },
  { value: 'NIT', label: 'NIT' },
  { value: 'other', label: 'Otro' },
];

export const PERSON_TYPE_OPTIONS: FieldOption[] = [
  { value: 'N', label: 'Natural' },
  { value: 'J', label: 'Jurídica' },
];

export const STATUS_OPTIONS: FieldOption[] = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'nurturing', label: 'Nurturing' },
];

export const PRIORITY_OPTIONS: FieldOption[] = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const SOURCE_OPTIONS: FieldOption[] = [
  { value: 'website', label: 'Website' },
  { value: 'landing_page', label: 'Landing Page' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'orbit_view', label: 'Maqueta Virtual' },
  { value: 'import', label: 'Importación' },
  { value: 'sala_ventas', label: 'Sala de Ventas' },
  { value: 'reunion_virtual', label: 'Reunión Virtual' },
  { value: 'llamada', label: 'Llamada Telefónica' },
  { value: 'feria', label: 'Feria Inmobiliaria' },
  { value: 'referral', label: 'Referido' },
  { value: 'portal', label: 'Portal Inmobiliario' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'partner', label: 'Partner' },
  { value: 'manual', label: 'Manual (CRM)' },
  { value: 'quote', label: 'Cotización' },
  { value: 'other', label: 'Other' },
];

export const BUSINESS_STATUS_OPTIONS: FieldOption[] = [
  { value: 'active', label: 'Activo' },
  { value: 'waiting', label: 'En espera' },
  { value: 'not_interested', label: 'No interesado' },
  { value: 'discarded', label: 'Descartado' },
  { value: 'unresponsive', label: 'Sin respuesta' },
  { value: 'reactivated', label: 'Reactivado' },
  { value: 'client', label: 'Cliente' },
];

export const CANCELLATION_CATEGORY_OPTIONS: FieldOption[] = [
  { value: 'FIN', label: 'Financiera' },
  { value: 'PRO', label: 'Producto / Proyecto' },
  { value: 'TIM', label: 'Timing / Decisión' },
  { value: 'CON', label: 'Contactabilidad' },
  { value: 'CAL', label: 'Calidad del Lead' },
  { value: 'EXP', label: 'Experiencia de Venta' },
];

export const ABANDONMENT_REASON_OPTIONS: FieldOption[] = [
  // Legacy
  { value: 'no_response', label: 'No responde' },
  { value: 'not_interested', label: 'No está interesado' },
  { value: 'price_too_high', label: 'El precio excede su presupuesto' },
  { value: 'location_not_ideal', label: 'La ubicación no le convence' },
  { value: 'not_ready_to_buy', label: 'No está listo para comprar' },
  { value: 'prefer_another_project', label: 'Prefiere otro proyecto' },
  { value: 'lost_to_competition', label: 'Se fue con la competencia' },
  { value: 'other', label: 'Otro motivo' },
  // Financiera
  { value: 'FIN-01', label: 'No tiene capacidad de endeudamiento' },
  { value: 'FIN-02', label: 'Cuota inicial muy alta' },
  { value: 'FIN-03', label: 'Precio fuera de presupuesto' },
  { value: 'FIN-04', label: 'No aprobó crédito hipotecario' },
  { value: 'FIN-05', label: 'Prefiere subsidio VIS/VIP' },
  { value: 'FIN-06', label: 'Problemas en centrales de riesgo' },
  // Producto / Proyecto
  { value: 'PRO-01', label: 'No le gustó la ubicación' },
  { value: 'PRO-02', label: 'Área o distribución no satisface' },
  { value: 'PRO-03', label: 'Fecha de entrega muy lejana' },
  { value: 'PRO-04', label: 'Faltan amenidades o zonas comunes' },
  { value: 'PRO-05', label: 'Prefiere entrega inmediata' },
  { value: 'PRO-06', label: 'Eligió otro proyecto / competencia' },
  { value: 'PRO-07', label: 'No le gustó el acabado o diseño' },
  // Timing / Decisión
  { value: 'TIM-01', label: 'No está listo para comprar ahora' },
  { value: 'TIM-02', label: 'Está evaluando otras opciones' },
  { value: 'TIM-03', label: 'Decisión depende de tercero' },
  { value: 'TIM-04', label: 'Cambio de planes personales' },
  { value: 'TIM-05', label: 'Desistió después de separar' },
  // Contactabilidad
  { value: 'CON-01', label: 'No responde (ghost)' },
  { value: 'CON-02', label: 'Datos de contacto incorrectos' },
  { value: 'CON-03', label: 'Bloqueó comunicación' },
  { value: 'CON-04', label: 'No se pudo agendar visita' },
  // Calidad del Lead
  { value: 'CAL-01', label: 'Solo buscaba información' },
  { value: 'CAL-02', label: 'Es competencia o corredor' },
  { value: 'CAL-03', label: 'Lead duplicado' },
  { value: 'CAL-04', label: 'Fuera de mercado geográfico' },
  { value: 'CAL-05', label: 'No es el perfil del proyecto' },
  { value: 'CAL-06', label: 'Lead de prueba o spam' },
  // Experiencia de Venta
  { value: 'EXP-01', label: 'Mala experiencia con asesor' },
  { value: 'EXP-02', label: 'Información inconsistente' },
  { value: 'EXP-03', label: 'Proceso de compra muy complejo' },
];

// ══════════════════════════════════════════════
// BUSINESS STATUS STYLES (label + color + bgColor)
// Single source of truth for consistent rendering
// ══════════════════════════════════════════════

export const BUSINESS_STATUS_STYLES: Record<string, { label: string; color: string; bgColor: string }> = {
  active:         { label: 'Activo',         color: '#10B981', bgColor: '#10B98120' },
  waiting:        { label: 'En espera',      color: '#7C3AED', bgColor: '#7C3AED20' },
  not_interested: { label: 'No interesado',  color: '#EC4899', bgColor: '#EC489920' },
  discarded:      { label: 'Descartado',     color: '#EF4444', bgColor: '#EF444420' },
  unresponsive:   { label: 'Sin respuesta',  color: '#9CA3AF', bgColor: '#9CA3AF20' },
  reactivated:    { label: 'Reactivado',     color: '#3B82F6', bgColor: '#3B82F620' },
  client:         { label: 'Cliente',        color: '#10B981', bgColor: '#10B98120' },
  abandoned:      { label: 'Cancelado',      color: '#EF4444', bgColor: '#EF444420' }, // Legacy
};

// ══════════════════════════════════════════════
// LABEL MAPS (for display / lookup)
// ══════════════════════════════════════════════

export const GENDER_LABELS = toLabelMap(GENDER_OPTIONS);
export const MARITAL_STATUS_LABELS = toLabelMap(MARITAL_STATUS_OPTIONS);
export const EDUCATION_LEVEL_LABELS = toLabelMap(EDUCATION_LEVEL_OPTIONS);
export const HOUSING_STATUS_LABELS = toLabelMap(HOUSING_STATUS_OPTIONS);
export const PET_TYPE_LABELS = toLabelMap(PET_TYPE_OPTIONS);
export const HAS_PETS_LABELS = toLabelMap(HAS_PETS_OPTIONS);
export const DOCUMENT_TYPE_LABELS = toLabelMap(DOCUMENT_TYPE_OPTIONS);
export const PERSON_TYPE_LABELS = toLabelMap(PERSON_TYPE_OPTIONS);
export const STATUS_LABELS = toLabelMap(STATUS_OPTIONS);
export const PRIORITY_LABELS = toLabelMap(PRIORITY_OPTIONS);
export const SOURCE_LABELS = toLabelMap(SOURCE_OPTIONS);
export const BUSINESS_STATUS_LABELS = toLabelMap(BUSINESS_STATUS_OPTIONS);
export const CANCELLATION_CATEGORY_LABELS = toLabelMap(CANCELLATION_CATEGORY_OPTIONS);
export const ABANDONMENT_REASON_LABELS = toLabelMap(ABANDONMENT_REASON_OPTIONS);

// ══════════════════════════════════════════════
// HELPER: Get label for any field + value
// ══════════════════════════════════════════════

const ALL_LABELS: Record<string, Record<string, string>> = {
  gender: GENDER_LABELS,
  marital_status: MARITAL_STATUS_LABELS,
  education_level: EDUCATION_LEVEL_LABELS,
  housing_status: HOUSING_STATUS_LABELS,
  pet_type: PET_TYPE_LABELS,
  has_pets: HAS_PETS_LABELS,
  document_type: DOCUMENT_TYPE_LABELS,
  person_type: PERSON_TYPE_LABELS,
  status: STATUS_LABELS,
  priority: PRIORITY_LABELS,
  source: SOURCE_LABELS,
  business_status: BUSINESS_STATUS_LABELS,
  cancellation_category: CANCELLATION_CATEGORY_LABELS,
  abandonment_reason: ABANDONMENT_REASON_LABELS,
};

/**
 * Universal label resolver.
 * getFieldLabel('gender', 'male') → 'Masculino'
 * getFieldLabel('housing_status', 'arriendo') → 'Arriendo'
 */
export function getFieldLabel(field: string, value: string): string {
  return ALL_LABELS[field]?.[value] || value;
}

// ══════════════════════════════════════════════
// ALL CHOICES MAP (for API sync / useLeadFieldChoices)
// ══════════════════════════════════════════════

export const ALL_FIELD_CHOICES: Record<string, FieldOption[]> = {
  gender: GENDER_OPTIONS,
  marital_status: MARITAL_STATUS_OPTIONS,
  education_level: EDUCATION_LEVEL_OPTIONS,
  housing_status: HOUSING_STATUS_OPTIONS,
  pet_type: PET_TYPE_OPTIONS,
  has_pets: HAS_PETS_OPTIONS,
  document_type: DOCUMENT_TYPE_OPTIONS,
  person_type: PERSON_TYPE_OPTIONS,
  status: STATUS_OPTIONS,
  priority: PRIORITY_OPTIONS,
  source: SOURCE_OPTIONS,
  business_status: BUSINESS_STATUS_OPTIONS,
  cancellation_category: CANCELLATION_CATEGORY_OPTIONS,
  abandonment_reason: ABANDONMENT_REASON_OPTIONS,
};
